import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  CarsAvailabilitySelectors,
  CAR_DATE_RANGE_SEPARATOR,
} from "@apac/components/cars";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";

interface CarDateRangeOptions {
  separator?: string;
  dateFormat?: DateTimeFormatStyle;
  timeFormat?: DateTimeFormatStyle;
}

interface UseCarDateRangeReturn {
  dateRange: {
    from: string;
    fromTime: string;
    until: string;
    untilTime: string;
  };
  numDays: number;
  dateFormattedString: string;
  dateTimeFormattedString: string;
}

const DATE_FORMAT = DateTimeFormatStyle.Custom({
  year: "numeric",
  month: "short",
  day: "numeric",
});
const TIME_FORMAT = DateTimeFormatStyle.ShortTime;

export const useCarDateRange = (
  options: CarDateRangeOptions = {}
): UseCarDateRangeReturn => {
  const {
    separator = CAR_DATE_RANGE_SEPARATOR,
    dateFormat = DATE_FORMAT,
    timeFormat = TIME_FORMAT,
  } = options;
  const { formatDateTime } = useI18nContext();

  const from = useSelector(
    CarsAvailabilitySelectors.getSearchFormPickupDateTime
  );
  const until = useSelector(
    CarsAvailabilitySelectors.getSearchFormDropoffDateTime
  );

  const numDays = useMemo(() => {
    if (from && until) {
      return dayjs(until).diff(dayjs(from), "day", false);
    }
    return 0;
  }, [from, until]);

  const dateRange = useMemo(
    () => ({
      from: from ? formatDateTime(from, dateFormat) : "",
      fromTime: from ? formatDateTime(from, timeFormat) : "",
      until: until ? formatDateTime(until, dateFormat) : "",
      untilTime: until ? formatDateTime(until, timeFormat) : "",
    }),
    [from, formatDateTime, dateFormat, timeFormat, until]
  );

  return {
    dateRange,
    numDays,
    dateFormattedString: `${dateRange.from}${separator}${dateRange.until}`,
    dateTimeFormattedString: `${dateRange.from} (${dateRange.fromTime})${separator}${dateRange.until} (${dateRange.untilTime})`,
  };
};
